/**
 * LazyCopilotKit - Lazy loaded CopilotKit wrapper
 *
 * This component wraps CopilotKit with React.lazy to prevent the 209KB
 * CopilotKit bundle from being included in the initial load.
 *
 * CopilotKit is only needed for AI-powered features like SmartCategorizer
 * and TransactionReview, which are already lazy-loaded themselves.
 *
 * In E2E test mode, CopilotKit is disabled to avoid recursion issues
 * caused by its network requirements with mocked backends.
 */

import React, { Suspense, lazy, ReactNode, useState, Component, ErrorInfo } from 'react';

// Detect E2E test mode - set by Playwright tests via localStorage or URL param
// This is a function to ensure it's called at render time, not module load time
const checkE2EMode = (): boolean => {
  if (typeof window === 'undefined') return false;
  // Check localStorage flag (set by E2E test setup)
  if (localStorage.getItem('e2e_test_mode') === 'true') return true;
  // Check URL param (backup method)
  if (window.location.search.includes('e2e=true')) return true;
  return false;
};

// Lazy load CopilotKit to avoid including it in the initial bundle
const CopilotKitLazy = lazy(() =>
  import('@copilotkit/react-core').then((module) => ({
    default: module.CopilotKit,
  }))
);

interface LazyCopilotKitProps {
  children: ReactNode;
  runtimeUrl: string;
}

/**
 * Minimal fallback that just renders children while CopilotKit loads.
 * This provides a seamless experience since CopilotKit is mostly a context provider.
 */
const CopilotKitFallback: React.FC<{ children: ReactNode }> = ({ children }) => (
  <>{children}</>
);

/**
 * Error boundary specifically for CopilotKit to prevent it from crashing the whole app.
 * If CopilotKit fails to load or initialize, we gracefully fall back to rendering children.
 */
interface CopilotKitErrorBoundaryProps {
  children: ReactNode;
  fallbackChildren: ReactNode;
}

interface CopilotKitErrorBoundaryState {
  hasError: boolean;
}

class CopilotKitErrorBoundary extends Component<CopilotKitErrorBoundaryProps, CopilotKitErrorBoundaryState> {
  constructor(props: CopilotKitErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): CopilotKitErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log but don't crash - CopilotKit is optional
    console.warn('CopilotKit failed to load, falling back to basic mode:', error.message);
    if (import.meta.env.DEV) {
      console.debug('CopilotKit error details:', errorInfo.componentStack);
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Render children without CopilotKit context
      return <>{this.props.fallbackChildren}</>;
    }
    return this.props.children;
  }
}

/**
 * LazyCopilotKit component that lazy loads the CopilotKit provider.
 *
 * Usage:
 * ```tsx
 * <LazyCopilotKit runtimeUrl="/api/copilotkit">
 *   <YourApp />
 * </LazyCopilotKit>
 * ```
 */
export const LazyCopilotKit: React.FC<LazyCopilotKitProps> = ({
  children,
  runtimeUrl,
}) => {
  // Check E2E mode at initial render to determine if we should skip CopilotKit
  // Using a state initialized via function ensures it runs at render time
  const [isE2E] = useState(() => checkE2EMode());

  // In E2E mode, skip CopilotKit to avoid network issues with mocked backends
  if (isE2E) {
    return <CopilotKitFallback>{children}</CopilotKitFallback>;
  }

  return (
    <CopilotKitErrorBoundary fallbackChildren={children}>
      <Suspense fallback={<CopilotKitFallback>{children}</CopilotKitFallback>}>
        <CopilotKitLazy runtimeUrl={runtimeUrl}>
          {children}
        </CopilotKitLazy>
      </Suspense>
    </CopilotKitErrorBoundary>
  );
};

export default LazyCopilotKit;
